import {
  IconButton,
  IconName,
  LinkButton,
  Switch,
  TextHighlight,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import { Box, Paper, TextField, Typography } from '@mui/material'
import React from 'react'
import { MediaType } from '../../../data/model/questionnaire'
import useQuestionnaireValidation from '../../../hooks/questionnaire/useQuestionnaireValidation'
import { isFocaldataUser } from '../../../utils/authUtils'
import { ConjointAttributeValueMedia } from './ConjointAttributeValueMedia'
import { ConjointAttribute } from './ConjointQuestion.container'
import { usePackageRestrictionsDialog } from './PackageRestrictions/PackageRestrictionsDialogProvider'
import PackageRestrictionsIndicatorIcon from './PackageRestrictions/PackageRestrictionsIndicatorIcon'

interface Props {
  attribute: ConjointAttribute
  showDeleteButton: boolean
  entryId: string
  questionnaireId: string
  hasMissingAppearanceImageErrors?: boolean
  onUpdateAttribute: (attributeId: string, name: string) => void
  onRemoveAttribute: (attributeId: string) => void
  onAddAttributeValue: (attributeId: string) => void
  onUpdateAttributeValue: (
    attributeId: string,
    valueId: string,
    value: string
  ) => void
  onRemoveAttributeValue: (attributeId: string, valueId: string) => void
  onToggleAppearance: (attributeId: string) => void
  onUploadAttributeValueMedia: (
    attributeId: string,
    optionId: string,
    mediaUrl: string,
    mediaName: string,
    mediaType: MediaType,
    renderedMediaUrl: string
  ) => Promise<void>
  onRemoveAttributeValueMedia: (
    attributeId: string,
    optionId: string
  ) => Promise<void>
}

export const ConjointAttributeItem: React.FC<Props> = ({
  attribute,
  showDeleteButton,
  entryId,
  questionnaireId,
  hasMissingAppearanceImageErrors,
  onUpdateAttribute,
  onRemoveAttribute,
  onAddAttributeValue,
  onUpdateAttributeValue,
  onRemoveAttributeValue,
  onToggleAppearance,
  onUploadAttributeValueMedia,
  onRemoveAttributeValueMedia
}) => {
  const { validateConjointAttributeName, validateConjointOptionValue } =
    useQuestionnaireValidation()
  const packageRestrictionsDialog = usePackageRestrictionsDialog()

  const attributeValidation = validateConjointAttributeName(
    entryId,
    attribute.id
  )
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        mb: 1,
        width: '100%',
        '&:hover': {
          backgroundColor: '#f8f9fa'
        }
      }}
    >
      <Box sx={{ p: 2, width: '100%' }}>
        {/* Header with attribute name and delete button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }}
        >
          <TextField
            value={attribute.name}
            onChange={(e) => onUpdateAttribute(attribute.id, e.target.value)}
            variant="outlined"
            size="small"
            placeholder="e.g., Color, Size, Price"
            sx={{ flex: 1, maxWidth: '300px' }}
            label="Attribute Name"
            data-testid={`attribute-name-${attribute.id}`}
            error={attributeValidation.hasError}
            helperText={attributeValidation.errorMessage}
          />
          {showDeleteButton && (
            <div data-testid={`delete-attribute-${attribute.id}`}>
              <IconButton
                iconName={IconName.DeleteOutline}
                onClick={() => onRemoveAttribute(attribute.id)}
                smallHoverShadowSize
              />
            </div>
          )}
        </Box>

        <Box sx={{ mb: 2, '& .MuiFormControlLabel-root': { ml: 0 } }}>
          <Switch
            label="Show as image (appearance attribute)"
            ariaLabel={`Toggle appearance attribute for ${attribute.name}`}
            checked={attribute.isAppearance}
            onChange={() => onToggleAppearance(attribute.id)}
          />
        </Box>

        {/* Values section */}
        <Box>
          <Typography
            variant="caption"
            sx={{ color: '#666', mb: 1, display: 'block' }}
          >
            Possible Values:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {attribute.values.map((value) => {
              const optionValidation = validateConjointOptionValue(
                entryId,
                attribute.id,
                value.id
              )
              const hasMissingImage =
                attribute.isAppearance &&
                hasMissingAppearanceImageErrors &&
                !value.media
              return (
                <Box
                  key={value.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <TextField
                    value={value.value}
                    onChange={(e) =>
                      onUpdateAttributeValue(
                        attribute.id,
                        value.id,
                        e.target.value
                      )
                    }
                    variant="outlined"
                    size="small"
                    placeholder="Enter value"
                    sx={{ flex: 1 }}
                    error={optionValidation.hasError || hasMissingImage}
                    helperText={
                      optionValidation.errorMessage ||
                      (hasMissingImage ? 'An image is required' : undefined)
                    }
                  />
                  {attribute.isAppearance && (
                    <ConjointAttributeValueMedia
                      questionnaireId={questionnaireId}
                      attributeId={attribute.id}
                      optionId={value.id}
                      media={value.media}
                      onUpload={onUploadAttributeValueMedia}
                      onRemove={onRemoveAttributeValueMedia}
                    />
                  )}
                  {isFocaldataUser() &&
                    packageRestrictionsDialog?.isOptionRestricted(value.id) && (
                      <PackageRestrictionsIndicatorIcon />
                    )}
                  {attribute.values.length > 2 && (
                    <div
                      data-testid={`delete-option-${attribute.id}-${value.id}`}
                    >
                      <IconButton
                        iconName={IconName.DeleteOutline}
                        onClick={() =>
                          onRemoveAttributeValue(attribute.id, value.id)
                        }
                        smallHoverShadowSize
                      />
                    </div>
                  )}
                </Box>
              )
            })}
          </Box>

          <LinkButton
            highlight={TextHighlight.Emphasis}
            size={TextSize.m}
            weight={TextWeight.SemiBold}
            onClick={() => onAddAttributeValue(attribute.id)}
            noPadding
          >
            Add Value
          </LinkButton>
        </Box>
      </Box>
    </Paper>
  )
}
